import { useState, useMemo } from 'react'
import { Link } from '@inertiajs/react'
import { motion, AnimatePresence } from 'framer-motion'
import SeoHead from '@/components/SeoHead'
import AnimatedNumber from '@/components/AnimatedNumber'
import MarketingFooter from '@/components/MarketingFooter'
import { calculateAmortization, type LoanType } from '@/lib/calculations'

// ─── Animation variants ────────────────────────────────────────────────────

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
}

const staggerContainer = {
  whileInView: { transition: { staggerChildren: 0.1 } },
  viewport: { once: true },
}

const staggerChild = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.4 },
}

// ─── FAQ data ──────────────────────────────────────────────────────────────

const faqItems = [
  {
    question: 'Will my spreadsheet data import correctly?',
    answer:
      'LendSolo supports CSV and Excel files with automatic column detection. The import tool shows you a preview before committing, and you can manually adjust any column mapping. Most imports complete in under 2 minutes.',
  },
  {
    question: 'What if I have a weird loan structure?',
    answer:
      'LendSolo supports standard amortizing, interest-only, and balloon loan structures \u2014 the three most common in private lending. Each calculates payments and amortization automatically.',
  },
  {
    question: 'Can I still export to a spreadsheet?',
    answer:
      'Yes. Payments, expenses, and tax reports can all be exported as CSV files. LendSolo also supports QuickBooks QBO export for direct accounting integration.',
  },
  {
    question: 'What does the free tier include?',
    answer:
      'Two loans with full payment tracking, dashboard, amortization schedules, and CSV export. No credit card required. Upgrade to Solo ($29/month) for up to 5 loans or Pro ($49/month) for up to 25.',
  },
]

// ─── Comparison data ───────────────────────────────────────────────────────

const comparisons = [
  { problem: 'My formulas broke', solution: 'Calculations are automatic' },
  {
    problem: 'I missed a payment date',
    solution: 'Dashboard + email reminders + overdue flags',
  },
  {
    problem: "My CPA needs numbers I can't produce",
    solution: 'One-click tax reports',
  },
  {
    problem: "I don't trust my own data",
    solution: 'Automatic principal/interest split, audit trail',
  },
]

// ─── Feature cards ─────────────────────────────────────────────────────────

const features = [
  {
    title: "Import, Don't Rebuild",
    description:
      'Upload your existing CSV or Excel file. LendSolo auto-maps your columns and imports your data. No manual re-entry.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
      </svg>
    ),
  },
  {
    title: 'The Dashboard You Wish You Had',
    description:
      'See your entire portfolio at a glance \u2014 total outstanding, payments due, overdue loans, and interest earned.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
      </svg>
    ),
  },
  {
    title: 'Payments That Split Themselves',
    description:
      "Record a payment and LendSolo automatically allocates principal and interest based on the loan's amortization schedule.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25v-.008zm2.498-6.75h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007v-.008zm2.504-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008v-.008zm2.498-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008v-.008zM8.25 6h7.5v2.25h-7.5V6zM12 2.25c-1.892 0-3.758.11-5.593.322C5.307 2.7 4.5 3.65 4.5 4.757V19.5a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25V4.757c0-1.108-.806-2.057-1.907-2.185A48.507 48.507 0 0012 2.25z" />
      </svg>
    ),
  },
  {
    title: 'Smart Guardrails',
    description:
      'LTV warnings when loans get risky. Overdue payment flags. Auto-calculated late fees. The safety net your spreadsheet never had.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
  },
]

// ─── FAQ Accordion Item ────────────────────────────────────────────────────

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="border-b border-gray-200">
      <button
        type="button"
        className="flex w-full items-center justify-between py-5 text-left"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="text-base font-medium text-gray-900 font-body">
          {question}
        </span>
        <svg
          className={`ml-4 h-5 w-5 flex-shrink-0 text-gray-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-sm leading-relaxed text-gray-600 font-body">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ─── Main Page Component ───────────────────────────────────────────────────

export default function SpreadsheetReplacement() {
  // Calculator state
  const [principal, setPrincipal] = useState(50000)
  const [annualRate, setAnnualRate] = useState(10)
  const [termMonths, setTermMonths] = useState(12)
  const [loanType, setLoanType] = useState<LoanType>('standard')

  const result = useMemo(() => {
    if (principal <= 0 || annualRate < 0 || termMonths <= 0) {
      return { monthlyPayment: 0, totalInterest: 0, totalCost: 0, schedule: [] }
    }
    return calculateAmortization(principal, annualRate, termMonths, new Date(), loanType)
  }, [principal, annualRate, termMonths, loanType])

  const faqSchema = faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  }))

  return (
    <>
      <SeoHead
        title="Stop Using Spreadsheets for Loan Tracking \u2014 LendSolo"
        description="Your lending spreadsheet was fine for 2 loans. Now it's a liability. LendSolo replaces it in minutes \u2014 import your data and never fix another formula."
        canonicalPath="/stop-using-spreadsheets-loan-tracking"
        schema={{
          '@graph': [
            {
              '@type': 'SoftwareApplication',
              name: 'LendSolo',
              description:
                'Loan management software that replaces spreadsheets for private lenders',
              url: 'https://lendsolo.com/stop-using-spreadsheets-loan-tracking',
              applicationCategory: 'FinanceApplication',
              operatingSystem: 'Web',
              offers: {
                '@type': 'Offer',
                price: '0',
                priceCurrency: 'USD',
                description: 'Free for up to 2 loans',
              },
            },
            {
              '@type': 'FAQPage',
              mainEntity: faqSchema,
            },
          ],
        }}
      />

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: '#0F1419' }}
            >
              <span className="text-emerald-400 font-bold text-sm">LS</span>
            </div>
            <span className="text-lg font-bold text-gray-900">LendSolo</span>
          </Link>
          <nav className="flex items-center gap-3">
            <Link
              href="/blog"
              className="hidden sm:inline-block text-sm text-gray-600 hover:text-gray-900 px-3 py-2"
            >
              Blog
            </Link>
            <Link
              href="/calculators"
              className="hidden sm:inline-block text-sm text-gray-600 hover:text-gray-900 px-3 py-2"
            >
              Calculators
            </Link>
            <Link
              href="/users/sign_in"
              className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2"
            >
              Sign In
            </Link>
            <Link
              href="/users/sign_up"
              className="text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded-lg transition-colors"
            >
              Get Started
            </Link>
          </nav>
        </div>
      </header>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section
        className="py-20 sm:py-28"
        style={{
          background: 'linear-gradient(165deg, #0E4D30 0%, #081C12 100%)',
        }}
      >
        <motion.div
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          {...fadeInUp}
        >
          <h1 className="text-4xl sm:text-5xl font-bold text-white font-display">
            Your Lending Spreadsheet Was Fine.
            <br />
            Until It Wasn&rsquo;t.
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 mt-6 max-w-2xl mx-auto font-body">
            You built it yourself. It worked for a while. Now it&rsquo;s one wrong
            formula away from costing you real money. Import it into LendSolo and
            stop worrying.
          </p>
          <Link
            href="/users/sign_up"
            className="inline-block mt-8 bg-[#34D399] hover:bg-emerald-400 text-gray-900 font-semibold px-8 py-3.5 rounded-lg transition-colors"
          >
            Start Free &rarr;
          </Link>
        </motion.div>
      </section>

      {/* ── Problem Narrative ───────────────────────────────────────────── */}
      <section className="bg-white py-16 sm:py-24">
        <motion.div
          className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8"
          {...fadeInUp}
        >
          <p className="text-lg text-gray-600 leading-[1.8] font-body">
            Let&rsquo;s be honest about what your spreadsheet actually looks like.
            Tab 1 has loan details, Tab 2 has payments (mostly), there&rsquo;s a
            third tab you started and never finished. The formulas reference
            deleted rows. You&rsquo;re not 100% sure the interest calculations are
            right anymore. Spreadsheets are brilliant general-purpose tools
            &mdash; they just weren&rsquo;t designed for loan management.
          </p>
        </motion.div>
      </section>

      {/* ── Visual Comparison ───────────────────────────────────────────── */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-12 font-display"
            {...fadeInUp}
          >
            The Spreadsheet Graveyard
          </motion.h2>

          <motion.div
            className="space-y-4"
            {...staggerContainer}
          >
            {comparisons.map((item, i) => (
              <motion.div
                key={i}
                className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-3 sm:gap-6 rounded-xl bg-white p-4 sm:p-5 shadow-sm"
                {...staggerChild}
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                    <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700 font-body">
                    {item.problem}
                  </span>
                </div>

                <div className="hidden sm:flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center">
                    <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-emerald-700 font-body">
                    {item.solution}
                  </span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Embedded Calculator ─────────────────────────────────────────── */}
      <section className="bg-white py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-10" {...fadeInUp}>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 font-display">
              Try it now &mdash; no signup needed
            </h2>
          </motion.div>

          <motion.div
            className="bg-gray-50 rounded-2xl p-6 sm:p-8"
            {...fadeInUp}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">
                  Loan Amount ($)
                </label>
                <input
                  type="number"
                  min={0}
                  value={principal}
                  onChange={(e) => setPrincipal(Number(e.target.value))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:border-emerald-500 focus:ring-emerald-500 font-body"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">
                  Annual Rate (%)
                </label>
                <input
                  type="number"
                  min={0}
                  step={0.25}
                  value={annualRate}
                  onChange={(e) => setAnnualRate(Number(e.target.value))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:border-emerald-500 focus:ring-emerald-500 font-body"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">
                  Term (months)
                </label>
                <input
                  type="number"
                  min={1}
                  value={termMonths}
                  onChange={(e) => setTermMonths(Number(e.target.value))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:border-emerald-500 focus:ring-emerald-500 font-body"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">
                  Loan Type
                </label>
                <select
                  value={loanType}
                  onChange={(e) => setLoanType(e.target.value as LoanType)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:border-emerald-500 focus:ring-emerald-500 font-body"
                >
                  <option value="standard">Standard Amortizing</option>
                  <option value="interest_only">Interest Only</option>
                  <option value="balloon">Balloon</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-xl p-5 text-center shadow-sm">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 font-body">
                  Monthly Payment
                </p>
                <AnimatedNumber
                  value={result.monthlyPayment}
                  prefix="$"
                  decimals={2}
                  className="text-2xl font-bold text-gray-900 font-display"
                />
              </div>
              <div className="bg-white rounded-xl p-5 text-center shadow-sm">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 font-body">
                  Total Interest
                </p>
                <AnimatedNumber
                  value={result.totalInterest}
                  prefix="$"
                  decimals={2}
                  className="text-2xl font-bold text-gray-900 font-display"
                />
              </div>
              <div className="bg-white rounded-xl p-5 text-center shadow-sm">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 font-body">
                  Total Cost
                </p>
                <AnimatedNumber
                  value={result.totalCost}
                  prefix="$"
                  decimals={2}
                  className="text-2xl font-bold text-gray-900 font-display"
                />
              </div>
            </div>

            <div className="text-center">
              <Link
                href="/users/sign_up"
                className="inline-block bg-[#34D399] hover:bg-emerald-400 text-gray-900 font-semibold px-8 py-3.5 rounded-lg transition-colors"
              >
                Ready to ditch the spreadsheet? &rarr; Start Free
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Solution Features ───────────────────────────────────────────── */}
      <section className="bg-gray-50 py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
            {...staggerContainer}
          >
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm"
                {...staggerChild}
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 font-display">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed font-body">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Social Proof Stats ──────────────────────────────────────────── */}
      <section className="bg-white py-16 sm:py-24">
        <motion.div
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-3 gap-6"
          {...staggerContainer}
        >
          {[
            { stat: 'Under 2 minutes', label: 'average import time' },
            { stat: '90%+', label: 'auto-mapped column headers' },
            { stat: '0 formulas', label: 'to maintain, ever' },
          ].map((item) => (
            <motion.div
              key={item.stat}
              className="text-center bg-gray-50 rounded-2xl p-8"
              {...staggerChild}
            >
              <p className="text-2xl sm:text-3xl font-bold text-emerald-600 font-display">
                {item.stat}
              </p>
              <p className="mt-2 text-sm text-gray-600 font-body">{item.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ── Related Pages ───────────────────────────────────────────────── */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            className="text-xl font-bold text-gray-900 mb-8 text-center font-display"
            {...fadeInUp}
          >
            You might also find these helpful
          </motion.h2>
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
            {...staggerContainer}
          >
            <motion.div {...staggerChild}>
              <Link
                href="/private-loan-tax-tracking"
                className="block bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <h3 className="text-base font-bold text-gray-900 mb-1 font-display">
                  Tax Tracking for Private Loans
                </h3>
                <p className="text-sm text-gray-600 font-body">
                  Your spreadsheet can&rsquo;t generate a 1098. LendSolo can.
                </p>
              </Link>
            </motion.div>
            <motion.div {...staggerChild}>
              <Link
                href="/manage-multiple-private-loans"
                className="block bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <h3 className="text-base font-bold text-gray-900 mb-1 font-display">
                  Manage Multiple Loans
                </h3>
                <p className="text-sm text-gray-600 font-body">
                  Two loans was easy. Five is where things break.
                </p>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────── */}
      <section className="bg-white py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-10 font-display"
            {...fadeInUp}
          >
            Frequently Asked Questions
          </motion.h2>
          <motion.div className="divide-y divide-gray-200" {...fadeInUp}>
            {faqItems.map((item) => (
              <FaqItem
                key={item.question}
                question={item.question}
                answer={item.answer}
              />
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Final CTA ───────────────────────────────────────────────────── */}
      <section
        className="py-20 sm:py-28"
        style={{
          background: 'linear-gradient(165deg, #0E4D30 0%, #081C12 100%)',
        }}
      >
        <motion.div
          className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          {...fadeInUp}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white font-display">
            Your spreadsheet served you well.
            <br />
            It&rsquo;s time to move on.
          </h2>
          <p className="text-lg text-gray-300 mt-6 font-body">
            Import your data. Get a real dashboard. Stop worrying about formulas.
          </p>
          <Link
            href="/users/sign_up"
            className="inline-block mt-8 bg-[#34D399] hover:bg-emerald-400 text-gray-900 font-semibold px-8 py-3.5 rounded-lg transition-colors"
          >
            Start Free &rarr;
          </Link>
        </motion.div>
      </section>

      <div className="mt-16">
        <MarketingFooter />
      </div>
    </>
  )
}
